/**
 * MotionStudioProgressCard.js — Admin-only live batch monitor
 * ────────────────────────────────────────────────────────────────
 * Renders a live progress card on the admin home dashboard showing
 * how many K-5 / Special Edition / Name & Lineage videos have been
 * generated by the background batch. Polls
 * GET /api/motion-studio/batch-progress every 6 seconds.
 *
 * Only mount for admin users (guard at the call site).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACKS = [
  { key: "k5", label: "K-5 Curriculum", color: "#FFD700" },
  { key: "special_edition", label: "Special Edition", color: "#DDA0DD" },
  { key: "name_and_lineage", label: "Name & Lineage", color: "#87CEEB" },
];

export default function MotionStudioProgressCard() {
  const [data, setData] = useState(null);
  const [library, setLibrary] = useState(null);   // iter 68 authentic-edition progress
  const [error, setError] = useState(null);
  const [previous, setPrevious] = useState(null);

  const fetchProgress = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Fan out to both endpoints in parallel — the batch endpoint
      // reports 3-track K-5 / SpecEd / N&L progress from the old
      // seeder; the library endpoint reports the v5-yisrael
      // authentic-edition rerender progress the user actually cares
      // about ("X / 549 clips at v5-yisrael").
      const [batch, lib] = await Promise.all([
        axios.get(`${API}/motion-studio/batch-progress`, { headers, timeout: 8000 })
          .then((r) => r.data)
          .catch(() => null),
        axios.get(`${API}/motion-studio/library?limit=1`, { headers, timeout: 8000 })
          .then((r) => r.data)
          .catch(() => null),
      ]);
      setPrevious(data);
      if (batch) setData(batch);
      if (lib) setLibrary(lib);
      setError(null);
    } catch (e) {
      setError(e?.message || "Fetch failed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    fetchProgress();
    const id = setInterval(fetchProgress, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data && !error) {
    return (
      <div
        data-testid="motion-studio-progress-card"
        style={_cardBase}
      >
        <div style={{ color: "#888", fontSize: "0.85rem" }}>
          Loading Motion Studio status…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="motion-studio-progress-card" style={_cardBase}>
        <div style={{ color: "#ff6b6b", fontSize: "0.85rem" }}>
          Couldn't reach Motion Studio: {String(error).slice(0, 80)}
        </div>
      </div>
    );
  }

  const allDone = TRACKS.every((t) => (data[t.key]?.pct || 0) >= 100);

  return (
    <div data-testid="motion-studio-progress-card" style={_cardBase}>
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "baseline", marginBottom: 14,
      }}>
        <div>
          <div style={{ color: "#FFD700", fontSize: "0.95rem", fontWeight: "bold" }}>
            🎬 Motion Studio Batch
          </div>
          <div style={{ color: "#888", fontSize: "0.75rem", marginTop: 3 }}>
            Live cartoon-helper video generation
          </div>
        </div>
        <div
          style={{
            fontSize: "0.7rem",
            color: allDone ? "#90EE90" : "#87CEEB",
            padding: "3px 10px",
            borderRadius: 6,
            background: allDone ? "#0a3a1a" : "#0a2a3a",
            whiteSpace: "nowrap",
          }}
        >
          {allDone ? "✓ All rendered" : `${data.total_cached || 0} cached`}
        </div>
      </div>

      {TRACKS.map((t) => {
        const track = data[t.key] || { rendered: 0, expected: 0, pct: 0 };
        const prevTrack = previous?.[t.key];
        const rising = prevTrack && track.rendered > prevTrack.rendered;
        return (
          <div key={t.key} style={{ marginBottom: 12 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: "0.78rem", marginBottom: 4,
            }}>
              <span style={{ color: "#ddd", fontWeight: 500 }}>{t.label}</span>
              <span style={{ color: rising ? "#90EE90" : "#aaa" }}>
                {rising && "▲ "}{track.rendered}/{track.expected} ({track.pct}%)
              </span>
            </div>
            <div style={{
              height: 8, background: "#1a1a1a",
              borderRadius: 4, overflow: "hidden",
            }}>
              <div style={{
                width: `${Math.min(100, track.pct)}%`,
                height: "100%",
                background: `linear-gradient(90deg, ${t.color} 0%, ${t.color}aa 100%)`,
                transition: "width 800ms ease",
              }} />
            </div>
          </div>
        );
      })}

      <div style={{
        color: "#555", fontSize: "0.65rem", marginTop: 8,
        borderTop: "1px solid #222", paddingTop: 8,
      }}>
        Updates every 6s · zero API cost · powered by YLC Motion Studio v1
      </div>

      {/* iter 68 — Authentic-edition (v5-yisrael) migration bar */}
      {library && library.total > 0 && (
        <div
          data-testid="motion-studio-authentic-progress"
          style={{
            marginTop: 12, paddingTop: 12,
            borderTop: "1px solid #FFD70033",
          }}
        >
          <div style={{
            display: "flex", justifyContent: "space-between",
            fontSize: "0.78rem", marginBottom: 4,
          }}>
            <span style={{ color: "#e8c66a", fontWeight: 600 }}>
              Eli lesson videos ({String(library.motion_version || "")})
            </span>
            <span style={{ color: (library.ready || 0) >= library.total ? "#90EE90" : "#e8c66a" }}>
              {library.ready || 0}/{library.total} clips
              {" "}({Math.round(((library.ready || 0) / library.total) * 100)}%)
            </span>
          </div>
          <div style={{
            height: 8, background: "#1a1a1a",
            borderRadius: 4, overflow: "hidden",
          }}>
            <div style={{
              width: `${Math.min(100, ((library.ready || 0) / library.total) * 100)}%`,
              height: "100%",
              background: "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)",
              transition: "width 800ms ease",
            }} />
          </div>
          {library.regenerating > 0 && (
            <div style={{ color: "#888", fontSize: "0.68rem", marginTop: 4 }}>
              {library.regenerating} playing the General Edition until their own Eli video is ready
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const _cardBase = {
  background: "linear-gradient(135deg, #14162e 0%, #0d0f22 100%)",
  border: "1px solid #FFD70044",
  borderRadius: 12,
  padding: "16px 18px",
  marginTop: 20,
  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
};
